<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="card" :class="{ 'full-height-card': fullHeightCard }">
    <a class="card-image recent-image" @click="handleNavigation">
      <img :src="imageUrl" class="image-preview-fit">
    </a>
    <div class="card-content">
      <div class="content image-info">
        <div class="image-header">
          <div class="image-actions">
            <b-tooltip :label="aiStatusHint" position="is-top" type="is-dark" v-if="aiStatus" class="mr-2">
              <div class="ai-status" style="display:flex; align-items:center; gap: 4px; cursor: help;">
                <span class="icon is-small has-text-grey-light">
                  <i class="fas fa-robot"></i>
                </span>
                <span class="icon is-small has-text-warning" v-if="aiStatus === 'Process'">
                  <i class="fas fa-spinner fa-spin"></i>
                </span>
                <span class="icon is-small has-text-danger" v-else-if="aiStatus === 'Error'">
                  <i class="fas fa-exclamation-circle"></i>
                </span>
                <span class="icon is-small has-text-success" v-else-if="aiStatus === 'Ready'">
                  <i class="fas fa-check-circle"></i>
                </span>
              </div>
            </b-tooltip>
            <button class="info-button" @click.prevent="$emit('show-details')" :title="$t('view-details')">
              <span class="icon is-small">
                <i class="fas fa-info-circle"></i>
              </span>
            </button>
          </div>
          <div class="image-name">{{ image.instanceFilename || image.imageName }}</div>
          <div class="label-container mt-2" v-if="!isBlindMode && !labelError && labelDataUrl">
            <img :src="labelDataUrl" class="label-preview" :title="$t('image-label')" @error="labelError = true">
          </div>
        </div>
        <div class="image-details">
          <div class="info-item" v-if="tissue">
            <span class="info-label">{{ $t('tissue') }}:</span>
            <span class="info-value">{{ tissue }}</span>
          </div>
          <div class="info-item" v-if="specimen">
            <span class="info-label">{{ $t('specimen') }}:</span>
            <span class="info-value">{{ specimen }}</span>
          </div>
          <div class="info-item" v-if="stain">
            <span class="info-label">{{ $t('stain') }}:</span>
            <span class="info-value">{{ stain }}</span>
          </div>
        </div>
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { changeImageUrlFormat } from '@/utils/image-utils';
import { get } from '@/utils/store-helpers';
import { appendShortTermToken } from '@/utils/token-utils.js';
import Cytomine from '@/api/cytomine';

export default {
  name: 'image-preview',
  data() {
    return {
      aiStatus: null, // 'Process', 'Error', 'Ready'
      labelError: false,
      labelDataUrl: null,
    };
  },
  props: {
    image: { type: Object },
    project: { type: Object, default: null },
    fullHeightCard: { type: Boolean, default: true },
    showProject: { type: Boolean, default: true },
    blindMode: { type: Boolean, default: false },
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    idImage() {
      return this.image.image || this.image.id; // if provided object is image consultation, image.image
    },
    idProject() {
      return (this.project) ? this.project.id : this.image.project;
    },
    isBlindMode() {
      return (this.project) ? this.project.blindMode : this.blindMode;
    },
    rawPreviewUrl() {
      return this.image.thumb || this.image.imageThumb;
    },
    previewUrl() {
      return changeImageUrlFormat(this.rawPreviewUrl);
    },
    imageUrl() {
      return appendShortTermToken(this.previewUrl, this.shortTermToken);
    },
    tissue() {
      // Return fixed value for now, can be made dynamic later
      return '';
    },
    specimen() {
      // Return fixed value for now, can be made dynamic later
      return '';
    },
    stain() {
      // Return fixed value for now, can be made dynamic later
      return '';
    },
    aiStatusHint() {
      if (!this.aiStatus) return '';
      if (this.aiStatus === 'Process') return 'AI is currently analyzing this image (Processing)...';
      if (this.aiStatus === 'Error') return 'An error occurred during AI analysis (Failed).';
      if (this.aiStatus === 'Ready') return 'AI analysis completed successfully (Ready).';
      return `AI Status: ${this.aiStatus}`;
    }
  },
  async created() {
    this.fetchJobStatus();
    this.fetchLabelImage();
  },
  beforeDestroy() {
    if (this.labelDataUrl) {
      URL.revokeObjectURL(this.labelDataUrl);
    }
  },
  watch: {
    idImage() {
      this.fetchLabelImage();
    }
  },
  methods: {
    async fetchJobStatus() {
      try {
        let response = await Cytomine.instance.api.get(`ai-algorithm-jobs?imageId=${this.idImage}`);
        let jobs = response.data;
        if (response.data.collection) jobs = response.data.collection;
        else if (!Array.isArray(jobs)) jobs = [jobs];

        if (jobs && jobs.length > 0) {
          // Identify the most recent job; sort by 'created' ascending to guarantee the last one is the newest.
          jobs.sort((a, b) => (a.created || 0) - (b.created || 0));
          let latestJob = jobs[jobs.length - 1];
          let status = latestJob.status;

          if (['Processing', 'Running', 'Starting'].includes(status)) this.aiStatus = 'Process';
          else if (['Error', 'Failed'].includes(status)) this.aiStatus = 'Error';
          else this.aiStatus = 'Ready';
        }
      } catch (error) {
        console.error("Failed to fetch jobs for image ", this.idImage, error);
      }
    },
    async fetchLabelImage() {
      if (this.isBlindMode) return;
      try {
        if (this.labelDataUrl) {
          URL.revokeObjectURL(this.labelDataUrl);
          this.labelDataUrl = null;
        }
        this.labelError = false;

        const url = `imageinstance/${this.idImage}/associated/label.jpg`;
        const { data } = await Cytomine.instance.api.get(url, { responseType: 'blob' });
        this.labelDataUrl = URL.createObjectURL(data);
      } catch (error) {
        console.error("Failed to fetch label image for ", this.idImage, error);
        this.labelError = true;
      }
    },
    handleNavigation() {
      let groupId = null;
      const projectTreeState = this.$store.state['project-tree'];

      if (projectTreeState.selectedItemType === 'imageGroup') {
        groupId = projectTreeState.selectedItem.id;
      }

      console.log('[DEBUG] ImagePreview Click: Current item type in store:', projectTreeState.selectedItemType);
      console.log('[DEBUG] ImagePreview Click: Dispatching imageGroup ID:', groupId);

      this.$store.dispatch('project-tree/setActiveImageGroupForViewer', groupId);
      this.$router.push(`/project/${this.idProject}/image/${this.idImage}`);
    }
  },
};
</script>

<style scoped lang="scss">
@import "../../assets/styles/dark-variables.scss";

.image-preview-fit {
  object-fit: contain;
  width: calc(100% - 1rem);
  height: auto;
  cursor: pointer;
  display: block;
  margin: 0.5rem auto 0;
  border: 1px solid rgba(190, 214, 238, 0.14);
  border-radius: 6px;
  background: #f8fafc;
  box-shadow: inset 0 0 0 1px rgba(8, 19, 31, 0.05);
}

.label-container {
  width: 100%;
  padding: 0.5rem 0;
}

.label-preview {
  object-fit: contain;
  width: 80%;
  max-height: 256px;
  height: auto;
  display: block;
  margin: 0 auto;
  opacity: 0.78;
  transition: opacity 0.2s;
  border-radius: 4px;
  border: 1px solid rgba(190, 214, 238, 0.14);

  &:hover {
    opacity: 1;
  }
}

.card.full-height-card {
  height: 100%;
  background: linear-gradient(180deg, rgba(222, 238, 255, 0.038), rgba(222, 238, 255, 0.014)) !important;
  border-color: rgba(190, 214, 238, 0.15);
  overflow: hidden;
}

.card-content {
  padding: 0.8rem;
  overflow-wrap: break-word;
}

.card-content a {
  font-weight: 600;
}

.blind-indication {
  font-size: 0.9em;
  text-transform: uppercase;
}

.image-info {
  color: $dark-text-secondary;
  font-size: 0.86rem;
  line-height: 1.45;
}

.image-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.image-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-height: 1.45rem;
  margin-bottom: 0.35rem;
}

.image-name {
  color: $dark-text-primary;
  font-weight: 700;
  flex: 1;
  word-break: break-all;
  letter-spacing: 0;
}

.info-button {
  background-color: rgba(222, 238, 255, 0.055);
  border: 1px solid rgba(222, 238, 255, 0.15);
  border-radius: 4px;
  padding: 0.22rem 0.44rem;
  cursor: pointer;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, transform 0.12s ease;
  color: $dark-text-secondary;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  &:hover {
    background-color: rgba($primary, 0.16);
    border-color: rgba($primary, 0.45);
    color: $primary;
  }

  &:active {
    transform: scale(0.95);
  }

  .icon {
    font-size: 0.9rem;
  }
}

.image-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-item {
  display: flex;
}

.info-label {
  font-weight: 700;
  margin-right: 5px;
  flex-shrink: 0;
  color: $dark-text-disabled;
}

.ai-status {
  padding: 0.12rem 0.32rem;
  border-radius: 999px;
  background: rgba(222, 238, 255, 0.052);
  border: 1px solid rgba(222, 238, 255, 0.12);
}

.info-value {
  flex-grow: 1;
}
</style>
